import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Decimal } from 'decimal.js';
import { Observable } from 'rxjs/internal/Observable';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { IIngredientWasteForAmend } from 'src/app/inventory/interfaces/ingredient-for-amend.interface';
import { IRequiredBackCountActivityModel } from 'src/app/settings/interfaces/required-back-count-activity-model.interface';
import { collectErrors, getDateFromISO, highlightRequiredControlsWithErrors, valueIsNumber } from '../../helpers/helpers';
import { ActivityTypesEnum } from '../../interfaces/ActivityTypesEnum';
import { AlertTypesEnum } from '../../interfaces/AlertTypesEnum';
import { IAmendTransaction, TransactionAmendment } from '../../interfaces/amend-transaction.interface';
import { AmendReason } from '../../interfaces/AmendReason';
import { AmendReasons } from '../../interfaces/AmendReasonsConstants';
import { ICompoundAmendRequestPayload } from '../../interfaces/compound-amend-request.interface';
import { DrugScheduleEnum } from '../../interfaces/DrugScheduleEnum ';
import { IInventory } from '../../interfaces/inventory.interface';
import { TransactionDateError } from '../../interfaces/IValidatedTransactionDate';
import { ITransaction } from '../../interfaces/transaction.interface';
import { FormattedDatePipe } from '../../pipes/formatted-date/formatted-date.pipe';
import { BackCountService } from '../../services/back-count.service';
import { TransactionsService } from '../../services/transactions.service';
import { HideAlibiWarning } from '../../states/auth/auth.actions';
import { GetInventoryOnHandForMoment, ValidateTransactionDateForAmend, ValidateTransactionDateForCompoundAmendIngredients } from '../../states/inventory/inventory.actions';
import { InventoryState } from '../../states/inventory/inventory.state';
import { PharmacyState } from '../../states/pharmacy/pharmacy.state';
import { GetAmendIngredientsWaste, GetAmendReasonsList, GetUnconfirmedPendingDispenseOwedQuantity } from '../../states/transactions/transactions.actions';
import { TransactionsState } from '../../states/transactions/transactions.state';
@UntilDestroy()
@Component({
  selector: 'app-amend-dialog',
  templateUrl: './amend-dialog.component.html',
  styleUrls: [
    './amend-dialog.component.scss',
    '../../../dashboard/components/user-board/dialogs/user-board-dialog.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AmendDialogComponent implements OnInit, AfterViewInit {
  @Input() show: boolean;

  private _transaction: ITransaction;
  @Input()
  get transaction(): ITransaction {
    return this._transaction;
  }
  set transaction(value: ITransaction) {
    this._transaction = value;
    if (!this._transaction) return;

    this.setFilteredAmendReasons();
  }

  dialogId = 'amend-dialog';

  @Input() originalTransactionId: string;

  @Output() amend: EventEmitter<IAmendTransaction> = new EventEmitter();
  @Output() compoundAmend: EventEmitter<ICompoundAmendRequestPayload> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  @ViewChild('header') header;
  @ViewChild('amendReasonDropdown') amendReasonDropdown;
  @ViewChild('addedQuantityInput') addedQuantityInput;
  @ViewChild('rxNumberInput') rxNumberInput;
  @ViewChild('patientNameInput') patientNameInput;
  @ViewChild('otherAmendReason') otherAmendReason;
  @ViewChild('cancelBtn') cancelBtn;
  @ViewChild('submitBtn') submitBtn;
  amendForm: UntypedFormGroup;
  compoundForm: UntypedFormGroup;

  amendReasons: any[];
  allAmendReasons: any[];

  AlertTypesEnum = AlertTypesEnum;

  getDateFromISO = getDateFromISO;

  ActivityTypesEnum = ActivityTypesEnum;

  showWitnessMessage: boolean; // remove

  currentInventoryCountForTransactionDrug: number;

  AmendReason = AmendReason;

  @Select(TransactionsState.amendReasonsList) amendReasonsList$: Observable<any[]>;
  @Select(TransactionsState.success) success$: Observable<boolean>;
  @Select(InventoryState.currentInventoryOnHand) inventory$: Observable<IInventory[]>;
  @Select(InventoryState.loading) loading$: Observable<boolean>;
  @Select(PharmacyState.backCountValue) backCountValue$: Observable<IRequiredBackCountActivityModel[]>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;
  @Select(TransactionsState.ingredientsAndWasteForTheCurrentAmend) ingredientsAndWasteForTheCurrentAmend$: Observable<IIngredientWasteForAmend[]>;


  backCountEnabled: boolean;

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private formattedDatePipe: FormattedDatePipe,
    private transactionsService: TransactionsService,
    private backCountService: BackCountService,
    private cdr: ChangeDetectorRef
  ) {
    this.amendForm = fb.group({
      transactionId: ['', []],
      amendReason: ['', [Validators.required]],
      addedQuantity: ['', []],
      rxNumber: ['', []],
      patientName: ['', []],
      transactionDateDisplayValue: formattedDatePipe.transform(
        new Date(),
        'MMM d a, HH:mm yyyy'
      ),
      transactionDate: [new Date(), [Validators.required]],
      amendReasonOtherDescription: '',
      isBackCountMatches: ''
    });
    if (this.store.selectSnapshot(PharmacyState.compoundingEnabled)) {
      this.compoundForm = this.fb.group({
        compound: this.fb.array([]), //array required for the table
        ingredients: this.fb.array([]),
        waste:this.fb.array([]),
      });
    }
  }

  ngOnInit(): void {
    if (
      !this.store.selectSnapshot(TransactionsState)?.amendReasonsList?.length
    ) {
      this.store.dispatch(new GetAmendReasonsList());
    }

    if (this.transaction?.activityType === ActivityTypesEnum.Compounded) {
      setTimeout(() => {
        this.store.dispatch(new GetAmendIngredientsWaste(this.transaction.id));
      });
    }


    this.amendReasonsList$
      .pipe(untilDestroyed(this))
      .subscribe((response: any[]) => {
        if (response && response.length) {
          this.allAmendReasons = response?.filter(x => x?.name !== 'Returned to wholesaler');
          this.setFilteredAmendReasons();
        }
      });

    this.amendForm.controls.transactionDate.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((date) => {
        if (date) {
          this.setCurrentInventoryForTimeAndUpdateValidators(date);
        }
      });

    this.setCurrentInventoryForTimeAndUpdateValidators(
      this.amendForm.controls.transactionDate.value
    );

    this.backCountValue$
    .pipe(untilDestroyed(this))
    .subscribe((activities: IRequiredBackCountActivityModel[]) => {
     this.backCountEnabled = this.backCountService.enabledForActivityAndSchedule(ActivityTypesEnum.Amendment, this.transaction.drug.schedule);
     if (this.backCountEnabled && this.transaction.activityType !== ActivityTypesEnum.Compounded) {
      this.amendForm.get('isBackCountMatches').setValidators(Validators.required);
      this.amendForm.get('isBackCountMatches').updateValueAndValidity();
     }
    })
    
    this.focusModal();
  }

  private setCurrentInventoryForTimeAndUpdateValidators(moment: Date): void {
    this.store
      .dispatch(
        new GetInventoryOnHandForMoment(this._transaction.drug.id, this.transaction.locationID ?? this.store.selectSnapshot(InventoryState.primaryLocationID), moment)
      )
      .pipe(untilDestroyed(this))
      .subscribe((_) => {
        const response = _?.inventory?.response;
        this.currentInventoryCountForTransactionDrug = response?.onHand || 0;
        const amendReason = this.amendForm.get('amendReason')?.value;
        if (valueIsNumber(amendReason)) {
          this.setValidatorsBasedOnTheReason(amendReason);
          this.cdr.markForCheck();
        }
      });
  }

  ngAfterViewInit(): void {
    if (this.transaction) {
      this.amendForm.get('transactionId').patchValue(this.transaction.id);
    }
  }

  changeReason(reason): void {
    this.setValidatorsBasedOnTheReason(reason);
  }

  public get formErrors(): any | null {
    const errors = collectErrors(this.amendForm);
    return JSON.stringify(errors);
  }

  amendTransaction(): void {
    if(this.amendForm.invalid || this.compoundForm?.invalid) {
      highlightRequiredControlsWithErrors(this.amendForm);
      highlightRequiredControlsWithErrors((this.compoundForm?.get('compound') as FormArray)?.controls[0] as FormGroup);
      (this.compoundForm?.get('ingredients') as FormArray)?.controls?.forEach(control => {
        highlightRequiredControlsWithErrors(control as FormGroup);
      });
      (this.compoundForm?.get('waste') as FormArray)?.controls?.forEach(control => {
        highlightRequiredControlsWithErrors(control as FormGroup);
      });

      return;
    }
    const inputValues = this.amendForm.getRawValue();
    const payload = {
      originalTransactionId: this.originalTransactionId || this.transaction.id,
      transactionDate: inputValues.transactionDate,
      addedQuantity:
         inputValues.addedQuantity || this.compoundForm?.get('compound')?.value[0]?.amended || this.getCalculatedAdjustment(),
      amendmentReason: inputValues.amendReason,
    };

    this.store
      .dispatch(new ValidateTransactionDateForAmend(payload))
      .pipe(untilDestroyed(this))
      .subscribe((resp) => {
        const response = resp?.inventory?.response;
        if (
          response?.success &&
          response.value?.transactionDateError === TransactionDateError.none
        ) {
          //continue with validating ingredients
          if (this.transaction.activityType === ActivityTypesEnum.Compounded) { 
            this.validateTransactionDateForCompoundIngredients();
          } else { //emit the regular amend
            const amendment = this.getTransactionAmendmentPayload();
            this.amend.emit(amendment);
          }
        } else if (response?.success) {
          this.transactionsService.handleTransactionDateErrors(response.value);
          this.markAddedQtyInvalid();
        }
      });
  }

  private validateTransactionDateForCompoundIngredients(): void {
      const compoundFormValue = this.compoundForm?.getRawValue();
      const payload = {
        transactionIDs: (compoundFormValue.ingredients?.map(x => x.id) as string[]).concat(compoundFormValue.waste?.map(x => x.id) || []),
        transactionDate: this.amendForm.get('transactionDate').value
      };
      this.store
        .dispatch(new ValidateTransactionDateForCompoundAmendIngredients(payload))
        .pipe(untilDestroyed(this))
        .subscribe((resp) => {
          const response = resp?.inventory?.response;
          if (
            response?.success &&
            response.value?.transactionDateError === TransactionDateError.none
          ) {
            const amendment: ICompoundAmendRequestPayload = {
              compoundId: this.transaction.id,
              transactionDate: this.amendForm.get('transactionDate')?.value,
              isCompoundBackCountMatches: compoundFormValue.compound[0].isBackCountMatches,
              amendReasonId: this.amendForm.get('amendReason')?.value,
              amendReasonOtherDescription: this.amendForm.get('amendReasonOtherDescription')?.value,
              ingredientsWaste: []
            };
            amendment.ingredientsWaste = amendment.ingredientsWaste.concat(compoundFormValue.ingredients?.map(x => ({id: x.id, isBackCountMatches: x.isBackCountMatches})));
            amendment.ingredientsWaste = amendment.ingredientsWaste.concat(compoundFormValue.waste?.map(x => ({id: x.id, isBackCountMatches: x.isBackCountMatches})));
            this.compoundAmend.emit(amendment);
          } else if (response?.success) {
            this.transactionsService.handleTransactionDateErrors(response.value);
          }
        });
  }
  

  private getTransactionAmendmentPayload(): TransactionAmendment {
    const inputValues = this.amendForm.getRawValue();

    const amendment = new TransactionAmendment();

    amendment.transactionId = this.transaction.id;
    amendment.amendReasonId = inputValues.amendReason;
    amendment.rxNumber = inputValues.rxNumber;
    amendment.patientName = inputValues.patientName;

    amendment.addedQuantity =
      inputValues.amendReason === 3
        ? this.getCalculatedAdjustment() * -1
        : this.getCalculatedAdjustment();
    amendment.addedQuantity =
      inputValues.amendReason === 0
        ? parseFloat(this.amendForm.get('addedQuantity')?.value)
        : amendment.addedQuantity;
    amendment.mostRecentTransactionId = this.getMostRecentTransaction()?.id;
    amendment.transactionDate = inputValues?.transactionDate;
    amendment.amendReasonOtherDescription = inputValues.amendReasonOtherDescription;
    amendment.isBackCountMatches = inputValues?.isBackCountMatches;

    return amendment;
  }

  setFilteredAmendReasons() {
    if (
      this._transaction == null ||
      this._transaction == undefined ||
      this.allAmendReasons == null ||
      this.allAmendReasons == undefined
    )
      return;
    const activityType = this.getMostRecentTransaction().activityType;
    if (activityType == ActivityTypesEnum.Amendment) {
      this.amendReasons = this.allAmendReasons.filter(
        (i) =>
          i.name == this._transaction.reason ||
          i.name == AmendReasons.RecordedIncorrectQuantity
      );
      return;
    }

    let newReasons = [...this.allAmendReasons];

    if (activityType !== ActivityTypesEnum.Dispensed) {
      newReasons.splice(3, 1); // Amend patient / Rx only available for dispenses
    }
    if (activityType === ActivityTypesEnum.Received) {
      newReasons = newReasons.filter(
        (x) => x.name !== AmendReasons.ReturnedToStock
      ); // Can't return to stock on a receive
    }

    if (activityType === ActivityTypesEnum.Compounded) {
      newReasons = newReasons.filter(r => r.name === AmendReasons.Other);
    }

    if (this.getMostRecentTransaction()?.drug?.schedule === DrugScheduleEnum.Compound && activityType !== ActivityTypesEnum.Compounded) {
      newReasons = this.allAmendReasons.filter(r => r.name === AmendReasons.Other || r.name === AmendReasons.ReturnedToStock);
    }
    
    this.amendReasons = newReasons;
  }

  getCalculatedAdjustment(): number {
    const amendReason = parseInt(this.amendForm.get('amendReason')?.value);
    let amendedAmount = parseFloat(
      this.amendForm.get('amendReason')?.value != 0
        ? this.transaction?.addedQuantity
        : this.amendForm.get('addedQuantity')?.value
    );

    if (!isNaN(amendReason) && amendReason === 0) {
      amendedAmount = this.getMostRecentAdjustment() * -1;
      return amendedAmount;
    } else if (!isNaN(amendReason) && amendReason === 4) {
      amendedAmount = this.amendForm.get('addedQuantity')?.value * -1;
    } else {
      amendedAmount = this.getMostRecentAdjustment() * -1;
    }
    let currentCount = this.getCurrentInventoryCountForTransactionDrug();
    let results =
      currentCount + amendedAmount < 0 ? currentCount * -1 : amendedAmount;
    return isNaN(results) ? 0 : results;
  }

  getMostRecentAdjustment(): number {
    if (this.transaction?.history?.length > 0)
      return this.transaction.history[0]?.addedQuantity;

    return 0;
  }

  getMostRecentTransaction(): ITransaction {
    if (this.transaction?.history?.length > 0)
      return this.transaction.history[0];

    return null;
  }

  getIncorrectQuantityCalculatedAvailableCount(): number {

    if (
      this.getMostRecentTransaction()?.activityType ===
      ActivityTypesEnum.Received || this.getMostRecentTransaction()?.activityType === ActivityTypesEnum.Compounded
    )
      return (
        this.getCurrentInventoryCountForTransactionDrug() -
        Math.abs(this.getCalculatedAdjustment())
      );

    return (
      this.getCurrentInventoryCountForTransactionDrug() +
      Math.abs(this.getCalculatedAdjustment())
    );
  }

  getIncorrectQuantityCalculatedOnHandCount(): number | string {
    const amendingQuantity = parseFloat(this.amendForm.get('addedQuantity')?.value);

    if (isNaN(amendingQuantity) && this.amendForm.getRawValue().amendReason === AmendReason.RecordedIncorrectQuantity) {
      return '';
    }

    if (isNaN(amendingQuantity)) {
      return this.getCurrentInventoryCountForTransactionDrug();
    }

    if (this.getMostRecentTransaction()?.activityType === ActivityTypesEnum.Received) {
      //available + input
      return (
        new Decimal(this.getIncorrectQuantityCalculatedAvailableCount()).add(new Decimal(amendingQuantity)).toNumber()
      );
    }
    return (
      new Decimal(this.getIncorrectQuantityCalculatedAvailableCount()).sub(new Decimal(amendingQuantity)).toNumber()
    );
  }

  getReturnToWholesalerCalculatedOnHandCount(): number {
    const amendingQuantity = parseFloat(this.amendForm.get('addedQuantity')?.value);
    if (isNaN(amendingQuantity)) {
      return (this.getCurrentInventoryCountForTransactionDrug());
    }
    //NOTE: Result 5.5 - 5.3 = 0.20000000000000018                  Result = 5.5 - 5.3 = 0.2
    //instead of using DEFAULT floating-point arithmetic, use arbitrary-precision arithmetic.
    return (new Decimal(this.getCurrentInventoryCountForTransactionDrug()).sub(new Decimal(amendingQuantity)).toNumber());
  }

  getCalculatedNewOnHand(): number {
    let results = this.getCurrentInventoryCountForTransactionDrug() + this.getCalculatedAdjustment();
    return isNaN(results)
      ? this.getCurrentInventoryCountForTransactionDrug()
      : results;
  }

  getCurrentInventoryCountForTransactionDrug(): number {
    return  new Decimal(this.currentInventoryCountForTransactionDrug).toNumber();
  }

  getTransactionCountDescription(): string {
    switch (this.transaction?.activityType) {
      case ActivityTypesEnum.Dispensed:
        return 'Dispensed Count';
      case ActivityTypesEnum.LostStolen:
        return 'Lost/Stolen Count';
      case ActivityTypesEnum.DamagedExpired:
        return 'Damaged/Expired Count';
      case ActivityTypesEnum.Amendment:
        return 'Amended Count';
      case ActivityTypesEnum.Received:
        return 'Received Count';
      case ActivityTypesEnum.Sold:
        return 'Sold Count';
      case ActivityTypesEnum.Audit:
        return 'Audit Count';
      default:
        return 'Original Count';
    }
  }

  dateTimeChanged(date): void {
    this.amendForm.get('transactionDate')?.setValue(date?.value);
  }

  clearAllValidators(): void {
    this.amendForm.get('addedQuantity')?.clearValidators();
    this.amendForm.get('rxNumber')?.clearValidators();
    this.amendForm.get('patientName')?.clearValidators();
    this.amendForm.get('amendReasonOtherDescription')?.clearValidators();
  }

  updateAllValidators(): void {
    this.amendForm.get('addedQuantity')?.updateValueAndValidity();
    this.amendForm.get('rxNumber')?.updateValueAndValidity();
    this.amendForm.get('patientName')?.updateValueAndValidity();
    this.amendForm.get('amendReasonOtherDescription')?.updateValueAndValidity();
  }

  markAddedQtyInvalid(): void {
    this.amendForm.get('addedQuantity').setErrors({invalid: true});
    this.cdr.markForCheck();
  }

  hideAlibiWarning(): void {
    this.showWitnessMessage = false; // remove
    this.store.dispatch(new HideAlibiWarning());
  }

  focusInput(inputName: string): void {
    setTimeout(
      () => this[inputName] && this[inputName]?.appInput?.nativeElement?.focus()
    );
  }

  cancelAmend(): void {
    this.amendForm.reset();
    this.amendForm.get('amendReason').patchValue(null); //TODO: Fix dropdown component to reset placeholder text when we clear form value
    this.cancel.emit();
  }

  isOtherReason(): boolean {
    return (
      this.amendForm.get('amendReason')?.value &&
      !this.isIncorrectQuantityReason() &&
      !this.isReturnToWholesalerReason() &&
      !this.isIncorrectRxPatientReason()
    );
  }

  isIncorrectQuantityReason(): boolean {
    return this.amendForm.get('amendReason')?.value === AmendReason.RecordedIncorrectQuantity;
  }

  isReturnToWholesalerReason(): boolean {
    return this.amendForm.get('amendReason')?.value === AmendReason.ReturnedToWholesaler;
  }

  isIncorrectRxPatientReason(): boolean {
    return this.amendForm.get('amendReason')?.value === AmendReason.RecordedIncorrectRxNumberAndOrPatientName;
  }

  now() {
    return new Date();
  }

  focusControls(): void {
    this.amendForm.valid ? 
    this.submitBtn.focus() :
    this.cancelBtn.focus();
  }

  datePickerTabKey(): void {
    const firstInputOfInventoryBlock = document.querySelector(`#${this.dialogId} .inventory-item input`) as HTMLElement | undefined;
    if (this.amendForm.value.amendReason === AmendReason.Other) this.focusInput('otherAmendReason');
    else if (firstInputOfInventoryBlock) firstInputOfInventoryBlock.focus();  
    else this.focusControls();
  }

  inputTabKey() {
    event.preventDefault();
    this.focusControls();
  }

  private focusModal(): void {
    //focus something in modal to start correct tabbing position
    setTimeout(() => {
      this.header.nativeElement.focus();
    });
  }

  setValidatorsForDispense() {
    let max = Math.abs(this.getIncorrectQuantityCalculatedAvailableCount());
    const mostRecent = this.getMostRecentTransaction();
    if (mostRecent?.pendingTransaction?.isC2KeepPartialDispense) {
      this.store.dispatch(new GetUnconfirmedPendingDispenseOwedQuantity(mostRecent.pendingTransaction.rxNumber, mostRecent.pendingTransaction.refillNumber))
      .pipe(take(1))
      .subscribe(_ => {
        const owed = this.store.selectSnapshot(TransactionsState)?.response;
        const owedPlusAmend = +owed + this.getCalculatedAdjustment();
        const maxValidator = owedPlusAmend > max ? Validators.max(max) : this.customMaxValidator(owedPlusAmend);

        this.amendForm.get('addedQuantity')
                ?.setValidators([
                  Validators.required,
                  maxValidator,
                  Validators.min(0)
                ]);
      })
    }
    
    this.amendForm.get('addedQuantity')
    ?.setValidators([
      Validators.required,
      Validators.max(max),
      Validators.min(0)
    ]);
  }

  customMaxValidator(max: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (Validators.max(max)(control)) {
        return { originalPendingTransactionCountExceeded: `Total dispense qty is ${this.getMostRecentTransaction()?.pendingTransaction?.fullDispenseQuantity}. Amending count cannot exceed ${max} owed.` };
      }
      return null;
    };
  }

  private setValidatorsBasedOnTheReason(reason): void {
    this.clearAllValidators();
    switch (reason.id) {
      case 0: // incorrect number
        const lowerBoundary =
          this.getIncorrectQuantityCalculatedAvailableCount() < 0
            ? Math.abs(this.getIncorrectQuantityCalculatedAvailableCount())
            : 0;
        this._transaction.activityType === ActivityTypesEnum.Received
          ? this.amendForm
              .get('addedQuantity')
              ?.setValidators([
                Validators.required,
                Validators.min(lowerBoundary),
              ])
          : this.setValidatorsForDispense()
        this.focusInput('addedQuantityInput');
        break;
      case 3: // incorrect rx number - no change to inventory
        this.amendForm.get('rxNumber')?.setValidators(Validators.required);
        this.amendForm.get('patientName')?.setValidators(Validators.required);
        this.focusInput('rxNumberInput');
        break;
      case 4: // return to wholesaler
        this.amendForm
          .get('addedQuantity')
          ?.setValidators([
            Validators.required,
            Validators.max(
              Math.abs(this.getIncorrectQuantityCalculatedAvailableCount())
            ),
            Validators.min(0),
          ]);
        this.focusInput('addedQuantityInput');
        break;
      case 5: // other
        this.amendForm
          .get('amendReasonOtherDescription')
          ?.setValidators([
            Validators.required,
            Validators.minLength(1)
          ]);
        this.focusInput('otherAmendReason');
        break;
    }
    this.updateAllValidators();
  }

}
